import { useState, useCallback, useEffect, Suspense, useMemo } from 'react';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useSpaceData } from '@/hooks/useSpaceData';
import { toast } from 'sonner';
import { AppShell3 } from '@/components/app-shell-3';
import { SortableToolCard, toolConfigs } from '@/components/SpaceToolCard';
import { getCustomToolIcon } from '@/config/tools';
import { ToolHeader } from '@/components/ToolHeader';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { PageTransition } from '@/components/PageTransition';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy
} from '@dnd-kit/sortable';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  MoreVertical,
  Edit,
  Wrench,
  UserPlus,
  Trash2,
  Bell,
  AlertTriangle
} from 'lucide-react';

// Lazy load modals - only load when needed
const ProjectSettingsModal = lazyWithRetry(() =>
  import('@/components/ProjectSettingsModal').then(m => ({ default: m.ProjectSettingsModal }))
);
const DeleteSpaceModal = lazyWithRetry(() =>
  import('@/components/DeleteSpaceModal').then(m => ({ default: m.DeleteSpaceModal }))
);

// Hex color mapping for ToolHeader (moved outside component for stability)
const TOOL_HEX_COLORS = {
  matrix: '#ef4444',
  team: '#3b82f6',
  team_compliance: '#10b981',
  site_compliance: '#059669',
  qualifications: '#f59e0b',
  bookings: '#a855f7',
  calendar: '#ec4899',
  checklists: '#10b981',
  tasks: '#10b981',
  chat: '#6366f1',
  docs: '#f59e0b',
  message_board: '#06b6d4',
  checkins: '#14b8a6',
  check_ins: '#14b8a6',
  attendance: '#8b5cf6',
  ppe: '#eab308'
};

export function ProjectDetailPage({ isStack = false }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { userProfile: authUserProfile, company } = useAuth();

  // Use optimized hook for all space data
  // Pass company.id to validate the space belongs to the user's current company
  const {
    space: project,
    teamMembers,
    accessUsers,
    toolOrder,
    toolPreviews,
    loading,
    error,
    accessDenied,
    refetch,
    saveToolOrder
  } = useSpaceData(projectId, authUserProfile?.id, company?.id);

  // Set page title with space name
  usePageTitle(project?.name || 'Space');

  // Build spaceTools for ToolHeader from project's enabled tools (including custom tools)
  const spaceTools = useMemo(() => {
    if (!project?.tools) return [];
    const toolsObj = typeof project.tools === 'string' ? JSON.parse(project.tools) : project.tools;

    // Get built-in tools
    const builtInTools = Object.entries(toolsObj)
      .filter(([key, enabled]) => enabled === true && key !== '_custom')
      .map(([toolKey]) => ({
        id: toolKey,
        tool_type: toolKey,
        name: toolConfigs[toolKey]?.name || toolKey,
        icon: toolConfigs[toolKey]?.icon,
        color: TOOL_HEX_COLORS[toolKey] || '#6b7280',
        isCustom: false
      }));

    // Get custom tools
    const customTools = (toolsObj._custom || [])
      .filter(ct => ct.enabled)
      .map(ct => ({
        id: `custom_${ct.id}`,
        tool_type: `custom_${ct.id}`,
        name: ct.name,
        icon: getCustomToolIcon(ct.icon),
        color: ct.color || '#6366f1',
        isCustom: true,
        customToolData: ct
      }));

    return [...builtInTools, ...customTools];
  }, [project?.tools]);

  // Build merged config for tool cards (includes both built-in and custom tool configs)
  const mergedToolConfigs = useMemo(() => {
    const configs = { ...toolConfigs };

    // Add custom tool configs
    if (project?.tools?._custom) {
      const customTools = Array.isArray(project.tools._custom) ? project.tools._custom : [];
      customTools.forEach(ct => {
        const toolKey = `custom_${ct.id}`;
        configs[toolKey] = {
          name: ct.name,
          icon: getCustomToolIcon(ct.icon),
          description: 'External embedded tool',
          color: `bg-gray-50 border-gray-200`,
          iconColor: 'text-gray-600',
          content: ct.url,
          isCustom: true,
          customToolData: ct
        };
      });
    }

    return configs;
  }, [project?.tools?._custom]);

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  // Modal states
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsMode, setSettingsMode] = useState('details');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [toolToRemove, setToolToRemove] = useState(null);
  const [isRemoveToolDialogOpen, setIsRemoveToolDialogOpen] = useState(false);

  // Memoized handlers
  const handleUpdateProject = useCallback(async (updatedProject) => {
    try {
      const { error } = await supabase
        .from('projects')
        .update({
          name: updatedProject.name,
          description: updatedProject.description,
          location: updatedProject.location,
          location_data: updatedProject.location_data,
          industry: updatedProject.industry,
          avatar: updatedProject.avatar,
          tools: JSON.stringify(updatedProject.tools),
          schedule: JSON.stringify(updatedProject.schedule),
          team_members: JSON.stringify(updatedProject.teamMembers),
          updated_date: new Date().toISOString()
        })
        .eq('id', projectId)
        .select()
        .single();

      if (error) {
        console.error('Error updating project:', error);
        return;
      }

      // Refetch to update cache
      refetch();
      setIsSettingsOpen(false);
    } catch (err) {
      console.error('Error in handleUpdateProject:', err);
    }
  }, [projectId, refetch]);

  const handleConfirmRemoveTool = useCallback(async () => {
    if (!toolToRemove || !project) return;

    const updatedTools = { ...project.tools, [toolToRemove.key]: false };
    await handleUpdateProject({
      ...project,
      tools: updatedTools
    });

    setIsRemoveToolDialogOpen(false);
    setToolToRemove(null);
  }, [toolToRemove, project, handleUpdateProject]);

  const handleRemoveTool = useCallback((tool) => {
    setToolToRemove(tool);
    setIsRemoveToolDialogOpen(true);
  }, []);

  const handleOpenSettings = useCallback((mode) => {
    setSettingsMode(mode);
    setIsSettingsOpen(true);
  }, []);

  const handleNavigateToAlertSettings = useCallback(() => {
    navigate(`/space/${projectId}/alert-settings`);
  }, [navigate, projectId]);

  const handleNavigateToCheckinAlerts = useCallback(() => {
    navigate(`/space/${projectId}/checkin-alerts`);
  }, [navigate, projectId]);

  const handleOpenDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = toolOrder.indexOf(active.id);
      const newIndex = toolOrder.indexOf(over.id);
      const newOrder = arrayMove(toolOrder, oldIndex, newIndex);
      saveToolOrder(newOrder);
    }
  }, [toolOrder, saveToolOrder]);

  if (loading) {
    return (
      <AppShell3 breadcrumbs={[]} pageTitle='' pageDescription=''>
        {/* ToolHeader skeleton */}
        <div className='w-full bg-white border-b border-gray-200'>
          <div className='flex items-center justify-between py-3 px-6'>
            <div className='flex items-center gap-3'>
              <Skeleton className='h-8 w-[52px] rounded-md' />
              <div className='flex items-center gap-2.5'>
                <Skeleton className='w-5 h-5 rounded' />
                <Skeleton className='h-4 w-24' />
                <span className='text-sm text-gray-300'>/</span>
                <Skeleton className='h-4 w-12' />
              </div>
            </div>
          </div>
        </div>

        <div className='max-w-6xl mx-auto space-y-8 pt-8 px-4 lg:px-6'>
          {/* Header */}
          <div className='text-center space-y-6'>
            <div className='flex flex-col items-center space-y-4'>
              <Skeleton className='h-16 w-16 rounded-full' />
              <Skeleton className='h-9 w-64' />
            </div>
            <div className='flex justify-center'>
              <div className='flex items-center gap-3'>
                <div className='flex -space-x-1'>
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className='h-6 w-6 rounded-full border-2 border-white' />
                  ))}
                </div>
                <Skeleton className='h-4 w-16' />
              </div>
            </div>
          </div>

          {/* Tools grid - 4 columns matching real layout */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(i => (
              <Card key={i} className='flex flex-col border-0 overflow-hidden'>
                <CardHeader className='pb-2'>
                  <div className='flex items-start gap-2'>
                    <Skeleton className='h-7 w-7 rounded-lg flex-shrink-0' />
                    <div className='flex-1 space-y-1.5 overflow-hidden'>
                      <Skeleton className='h-4 w-24' />
                      <Skeleton className='h-3 w-full' />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className='flex-1 pt-0'>
                  <div className='border-t pt-3 space-y-3'>
                    {/* Simulate stat badges row */}
                    <div className='flex gap-2'>
                      <Skeleton className='h-10 w-14 rounded-lg' />
                      <Skeleton className='h-10 w-14 rounded-lg' />
                      <Skeleton className='h-10 w-14 rounded-lg' />
                    </div>
                    {/* Simulate progress/info lines */}
                    <Skeleton className='h-3 w-3/4' />
                    <Skeleton className='h-3 w-1/2' />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </AppShell3>
    );
  }

  if (!project) {
    return (
      <AppShell3
        breadcrumbs={[{ label: 'Space Not Found' }]}
        pageTitle='Space Not Found'
      >
        <div className='text-center py-12'>
          <p>Space not found</p>
          <Button onClick={() => navigate('/dashboard')} className='mt-4'>
            Back to Dashboard
          </Button>
        </div>
      </AppShell3>
    );
  }

  return (
    <PageTransition>
      <AppShell3 breadcrumbs={[]} pageTitle='' pageDescription=''>
        {/* ToolHeader with space tools */}
        <ToolHeader
          spaceName={project.name}
          spaceImage={project.avatar}
          toolName="Space"
          currentTool="overview"
          spaceTools={spaceTools}
        />

        <div className='max-w-6xl mx-auto space-y-8 pt-8'>
          {/* Project Header */}
          <div className='text-center space-y-6'>
            {/* Project Avatar and Title */}
            <div className='flex flex-col items-center space-y-4'>
              <Avatar className='h-16 w-16'>
                <AvatarImage src={project.avatar} alt={project.name} />
                <AvatarFallback className='text-xl'>
                  {project.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className='relative inline-block'>
                <h1 className='text-3xl font-bold'>{project.name}</h1>
                {/* Settings Dropdown - positioned absolutely */}
                <div className='absolute top-1/2 -translate-y-1/2 -right-10'>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='h-8 w-8 p-0 hover:bg-gray-200/70 rounded-md transition-all flex items-center justify-center'
                      >
                        <MoreVertical className='h-5 w-5 text-muted-foreground hover:text-foreground transition-colors' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end' className='w-56'>
                      <DropdownMenuItem onClick={() => handleOpenSettings('details')}>
                        <Edit className='h-4 w-4 mr-2' />
                        Edit Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleOpenSettings('tools')}>
                        <Wrench className='h-4 w-4 mr-2' />
                        Edit Tools
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleOpenSettings('access')}>
                        <UserPlus className='h-4 w-4 mr-2' />
                        Edit Access
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleNavigateToAlertSettings}>
                        <Bell className='h-4 w-4 mr-2' />
                        Alert Settings
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleNavigateToCheckinAlerts}>
                        <AlertTriangle className='h-4 w-4 mr-2' />
                        Check-In Alert Settings
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleOpenDeleteModal}
                        className='text-red-600 focus:text-red-600'
                      >
                        <Trash2 className='h-4 w-4 mr-2' />
                        Delete Space
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>

            {/* Who Has Access */}
            <div className='flex flex-col items-center space-y-2'>
              {(accessUsers.admins.length > 0 || accessUsers.managers.length > 0) && (
                <TooltipProvider delayDuration={0}>
                  <div className='flex flex-row items-center gap-3'>
                    {/* Admins */}
                    {accessUsers.admins.length > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className='flex items-center gap-1 cursor-pointer'>
                            <div className='flex -space-x-1'>
                              {accessUsers.admins.slice(0, 3).map((admin) => (
                                <Avatar key={admin.id} className='h-6 w-6 border border-white'>
                                  <AvatarImage src={admin.avatar} alt={admin.name} className='object-cover' />
                                  <AvatarFallback className='text-[10px]'>{admin.initials}</AvatarFallback>
                                </Avatar>
                              ))}
                              {accessUsers.admins.length > 3 && (
                                <Avatar className='h-6 w-6 border border-white bg-gray-200'>
                                  <AvatarFallback className='text-[10px]'>+{accessUsers.admins.length - 3}</AvatarFallback>
                                </Avatar>
                              )}
                            </div>
                            <span className='text-xs text-muted-foreground'>
                              {accessUsers.admins.length} Admin{accessUsers.admins.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side='top' sideOffset={10}>
                          <div className='space-y-2'>
                            <p className='text-xs font-semibold'>Admins (full access)</p>
                            {accessUsers.admins.map(admin => (
                              <div key={admin.id} className='flex items-center gap-2'>
                                <Avatar className='h-5 w-5'>
                                  <AvatarImage src={admin.avatar} alt={admin.name} />
                                  <AvatarFallback className='text-[10px]'>{admin.initials}</AvatarFallback>
                                </Avatar>
                                <span className='text-xs'>{admin.name}</span>
                              </div>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {/* Managers */}
                    {accessUsers.managers.length > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className='flex items-center gap-1 cursor-pointer'>
                            <div className='flex -space-x-1'>
                              {accessUsers.managers.slice(0, 3).map((manager) => (
                                <Avatar key={manager.id} className='h-6 w-6 border border-white'>
                                  <AvatarImage src={manager.avatar} alt={manager.name} className='object-cover' />
                                  <AvatarFallback className='text-[10px]'>{manager.initials}</AvatarFallback>
                                </Avatar>
                              ))}
                              {accessUsers.managers.length > 3 && (
                                <Avatar className='h-6 w-6 border border-white bg-gray-200'>
                                  <AvatarFallback className='text-[10px]'>+{accessUsers.managers.length - 3}</AvatarFallback>
                                </Avatar>
                              )}
                            </div>
                            <span className='text-xs text-muted-foreground'>
                              {accessUsers.managers.length} Manager{accessUsers.managers.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side='top' sideOffset={10}>
                          <div className='space-y-2'>
                            <p className='text-xs font-semibold'>Assigned Managers</p>
                            {accessUsers.managers.map(manager => (
                              <div key={manager.id} className='flex items-center gap-2'>
                                <Avatar className='h-5 w-5'>
                                  <AvatarImage src={manager.avatar} alt={manager.name} />
                                  <AvatarFallback className='text-[10px]'>{manager.initials}</AvatarFallback>
                                </Avatar>
                                <span className='text-xs'>{manager.name}</span>
                              </div>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TooltipProvider>
              )}
            </div>
          </div>

          {/* Tools Grid - Centered flex layout like dashboard */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={toolOrder}
              strategy={rectSortingStrategy}
            >
              <div className='flex justify-center w-full'>
                <div className='flex flex-wrap justify-center gap-4 w-full max-w-7xl'>
                  <AnimatePresence mode="popLayout">
                    {toolOrder.map(toolKey => (
                      <motion.div
                        key={toolKey}
                        layout
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{
                          layout: { type: 'spring', stiffness: 300, damping: 30 },
                          opacity: { duration: 0.2 },
                          scale: { duration: 0.2 }
                        }}
                        className='flex w-full sm:w-[calc(50%-8px)] lg:w-[calc(25%-12px)] max-w-[300px]'
                      >
                        <SortableToolCard
                          toolKey={toolKey}
                          config={mergedToolConfigs[toolKey]}
                          projectId={projectId}
                          onRemoveTool={handleRemoveTool}
                          previewData={toolPreviews[toolKey]}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            </SortableContext>
          </DndContext>

          {/* Lazy-loaded Modals - only render when open */}
          {isSettingsOpen && (
            <Suspense fallback={null}>
              <ProjectSettingsModal
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                project={project}
                onUpdateProject={handleUpdateProject}
                onSaveSuccess={refetch}
                initialMode={settingsMode}
              />
            </Suspense>
          )}

          {isDeleteModalOpen && (
            <Suspense fallback={null}>
              <DeleteSpaceModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                space={project}
              />
            </Suspense>
          )}

          {/* Remove Tool Confirmation Dialog */}
          {isRemoveToolDialogOpen && (
            <Dialog
              open={isRemoveToolDialogOpen}
              onOpenChange={setIsRemoveToolDialogOpen}
            >
              <DialogContent className='sm:max-w-[425px]'>
                <DialogHeader>
                  <DialogTitle>Remove Tool?</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to remove{' '}
                    <strong>{toolToRemove?.name}</strong> from this space? You can
                    re-enable it later from space settings.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant='outline'
                    onClick={() => {
                      setIsRemoveToolDialogOpen(false);
                      setToolToRemove(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button variant='destructive' onClick={handleConfirmRemoveTool}>
                    Remove Tool
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </AppShell3>
    </PageTransition>
  );
}
